import React, { useState, useEffect, useCallback } from 'react';
import { invitePlansApi } from './inviteplansapi';

// ─────────────────────────────────────────────
// Утиліти
// ─────────────────────────────────────────────

const STATUS_LABELS = {
    planning:   { label: 'Планування',  cls: 'bg-warning text-dark' },
    planned:    { label: 'Заплановано', cls: 'bg-info text-dark' },
    validating: { label: 'Валідація',   cls: 'bg-warning text-dark' },
    validated:  { label: 'Валідовано',  cls: 'bg-info text-dark' },
    committing: { label: 'Запис',       cls: 'bg-warning text-dark' },
    committed:  { label: 'Завершено',   cls: 'bg-success' },
    failed:     { label: 'Помилка',     cls: 'bg-danger' },
    cancelled:  { label: 'Скасовано',   cls: 'bg-secondary' },
};

const TRIGGER_LABELS = {
    scheduler: 'Автоматично',
    manual:    'Вручну',
    api:       'API',
};

function formatDate(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatDuration(ms) {
    if (!ms) return '—';
    if (ms < 1000) return ms + ' мс';
    if (ms < 60000) return (ms / 1000).toFixed(1) + ' с';
    return Math.floor(ms / 60000) + ' хв ' + Math.floor((ms % 60000) / 1000) + ' с';
}

function FillRateBar({ value }) {
    const pct = Math.min(100, Math.max(0, value || 0));
    const color = pct >= 80 ? '#198754' : pct >= 50 ? '#ffc107' : '#dc3545';
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, height: 6, background: '#e9ecef', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: pct + '%', height: '100%', background: color, borderRadius: 3, transition: 'width 0.4s' }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 600, color, minWidth: 36 }}>{pct}%</span>
        </div>
    );
}

function StatusBadge({ status }) {
    const s = STATUS_LABELS[status] || { label: status, cls: 'bg-secondary' };
    return <span className={'badge ' + s.cls} style={{ fontSize: 12 }}>{s.label}</span>;
}

function SortIcon({ col, sortConfig }) {
    if (sortConfig.key !== col) return <span style={{ color: '#ccc', marginLeft: 4 }}>⇅</span>;
    return <span style={{ marginLeft: 4 }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>;
}

function useSort(data, defaultKey) {
    const [sortConfig, setSortConfig] = useState({ key: defaultKey, direction: 'desc' });

    const sorted = React.useMemo(() => {
        if (!data) return [];
        return [...data].sort((a, b) => {
            const av = a[sortConfig.key], bv = b[sortConfig.key];
            if (av == null) return 1;
            if (bv == null) return -1;
            const cmp = av < bv ? -1 : av > bv ? 1 : 0;
            return sortConfig.direction === 'asc' ? cmp : -cmp;
        });
    }, [data, sortConfig]);

    const toggle = useCallback((key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    }, []);

    return { sorted, sortConfig, toggle };
}

// ─────────────────────────────────────────────
// Спільні: Spinner, Error, Empty
// ─────────────────────────────────────────────

function Spinner() {
    return (
        <div className="d-flex align-items-center gap-2 py-3 text-muted">
            <div className="spinner-border spinner-border-sm" role="status" />
            <span>Завантаження...</span>
        </div>
    );
}

function ErrorMsg({ msg }) {
    return <div className="alert alert-danger py-2">{msg || 'Помилка завантаження'}</div>;
}

function Empty({ text }) {
    return <div className="text-muted py-4 text-center">{text || 'Немає даних'}</div>;
}

// ─────────────────────────────────────────────
// Модальне вікно деталей плану
// ─────────────────────────────────────────────

// ─────────────────────────────────────────────
// Допоміжні константи (специфічні для PlanModal)
// ─────────────────────────────────────────────

const PLACE_TYPE_LABELS_PM = {
    'standart':       'Загальне',
    'without-bed':    'Короткотривале',
    'inclusive':      'Інклюзивне',
    'inclusiveShort': 'Інкл. коротке',
};

const ERROR_TYPE_LABELS_PM = {
    'request_status_changed': 'Змінено статус',
    'vacancy_gone':           'Місце зайнято',
    'request_not_found':      'Заявка не знайдена',
    'kindergarten_not_found': 'Садок не знайдено',
    'group_not_found':        'Група не знайдена',
};

const INVITE_STATUS_LABELS_PM = {
    planned:   'Заплановано',
    committed: 'Відправлено',
    skipped:   'Пропущено',
    error:     'Помилка',
};

const INVITE_STATUS_CLS_PM = {
    planned:   'bg-info text-dark',
    committed: 'bg-success',
    skipped:   'bg-secondary',
    error:     'bg-danger',
};

/**
 * Розрахунок віку дитини на 30 вересня заданого навчального року —
 * та сама точка відліку що використовується при доборі заявок.
 */
function computeAgeAtPeriod(birthDateStr, periodStartYear) {
    if (!birthDateStr || !periodStartYear) return null;
    const birth = new Date(birthDateStr);
    const calc  = new Date(periodStartYear, 8, 30);

    let years  = calc.getFullYear()  - birth.getFullYear();
    let months = calc.getMonth() - birth.getMonth();
    if (calc.getDate() < birth.getDate()) months--;
    if (months < 0) { years--; months += 12; }

    return { years, months };
}

function formatAge(age) {
    if (!age) return '—';
    const parts = [];
    if (age.years > 0)  parts.push(age.years + ' р');
    if (age.months > 0) parts.push(age.months + ' міс');
    return parts.length ? parts.join(' ') : '0 міс';
}

function formatBirthDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('uk-UA');
}

function ageBracketLabel(from, to) {
    return `${from} р. — ${to} р.`;
}

/**
 * Агрегація масиву слотів у єдиний обʼєкт статистики.
 */
function aggregateSlotsList(slotsArr) {
    const totalVacancies  = slotsArr.reduce((s, x) => s + x.totalVacancies, 0);
    const filledVacancies = slotsArr.reduce((s, x) => s + x.assignedCount, 0);
    const requestsInQueue = slotsArr.reduce((s, x) => s + x.requestsInQueue, 0);
    return {
        totalVacancies,
        filledVacancies,
        emptyVacancies: totalVacancies - filledVacancies,
        fillRate: totalVacancies ? Math.round(filledVacancies / totalVacancies * 100) : 0,
        requestsInQueue,
    };
}

/**
 * Розбивка масиву слотів по ключу (placeType або віковий діапазон).
 */
function breakdownSlotsBy(slotsArr, keyFn, labelFn) {
    const map = {};
    for (const slot of slotsArr) {
        const key = keyFn(slot);
        if (!map[key]) {
            map[key] = { key, label: labelFn(slot), totalVacancies: 0, filledVacancies: 0, requestsInQueue: 0 };
        }
        map[key].totalVacancies  += slot.totalVacancies;
        map[key].filledVacancies += slot.assignedCount;
        map[key].requestsInQueue += slot.requestsInQueue;
    }
    return Object.values(map).map(r => ({
        ...r,
        emptyVacancies: r.totalVacancies - r.filledVacancies,
        fillRate: r.totalVacancies ? Math.round(r.filledVacancies / r.totalVacancies * 100) : 0,
    }));
}

// ─────────────────────────────────────────────
// Дрібні UI-примітиви для дерева
// ─────────────────────────────────────────────

function ExpandArrow({ expanded }) {
    return <span style={{ display: 'inline-block', width: 16, color: '#6c757d' }}>{expanded ? '▾' : '▸'}</span>;
}

function MiniStats({ agg }) {
    return (
        <div className="d-flex align-items-center gap-3" style={{ fontSize: 12, color: '#495057' }}>
            <span>Вакансій: <b>{agg.totalVacancies}</b></span>
            <span style={{ color: '#198754' }}>Заповнено: <b>{agg.filledVacancies}</b></span>
            <span style={{ color: agg.emptyVacancies > 0 ? '#dc3545' : '#6c757d' }}>Порожніх: <b>{agg.emptyVacancies}</b></span>
            <span style={{ minWidth: 110 }}><FillRateBar value={agg.fillRate} /></span>
        </div>
    );
}

function BreakdownTable({ rows, keyLabel }) {
    if (!rows || rows.length === 0) return <Empty text="Немає даних" />;
    return (
        <table className="table table-sm mb-0" style={{ fontSize: 12, background: '#fff' }}>
            <thead>
                <tr style={{ color: '#6c757d' }}>
                    <th style={{ padding: '4px 10px', fontWeight: 500 }}>{keyLabel}</th>
                    <th style={{ padding: '4px 10px', fontWeight: 500 }}>Вакансій</th>
                    <th style={{ padding: '4px 10px', fontWeight: 500 }}>Заповнено</th>
                    <th style={{ padding: '4px 10px', fontWeight: 500 }}>Порожніх</th>
                    <th style={{ padding: '4px 10px', fontWeight: 500 }}>Заповненість</th>
                    <th style={{ padding: '4px 10px', fontWeight: 500 }}>Заявок</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r, i) => (
                    <tr key={i}>
                        <td style={{ padding: '4px 10px' }}>{r.label}</td>
                        <td style={{ padding: '4px 10px' }}>{r.totalVacancies}</td>
                        <td style={{ padding: '4px 10px', color: '#198754' }}>{r.filledVacancies}</td>
                        <td style={{ padding: '4px 10px', color: r.emptyVacancies > 0 ? '#dc3545' : '#6c757d' }}>{r.emptyVacancies}</td>
                        <td style={{ padding: '4px 10px', minWidth: 120 }}><FillRateBar value={r.fillRate} /></td>
                        <td style={{ padding: '4px 10px' }}>{r.requestsInQueue}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

/**
 * Таблиця дітей запрошених в конкретну групу (усі типи місць разом).
 */
function InvitesTable({ invites, periodStartYear }) {
    if (!invites || invites.length === 0) return <Empty text="Немає запрошень" />;
    return (
        <table className="table table-sm mb-0" style={{ fontSize: 12, background: '#fff' }}>
            <thead>
                <tr style={{ color: '#6c757d' }}>
                    <th style={{ padding: '4px 10px', fontWeight: 500 }}>№ заявки</th>
                    <th style={{ padding: '4px 10px', fontWeight: 500 }}>Дитина</th>
                    <th style={{ padding: '4px 10px', fontWeight: 500 }}>Свідоцтво</th>
                    <th style={{ padding: '4px 10px', fontWeight: 500 }}>Тип місця</th>
                    <th style={{ padding: '4px 10px', fontWeight: 500 }}>Дата народження</th>
                    <th style={{ padding: '4px 10px', fontWeight: 500 }}>Вік</th>
                    <th style={{ padding: '4px 10px', fontWeight: 500 }}>Статус</th>
                </tr>
            </thead>
            <tbody>
                {invites.map((inv, i) => {
                    const child = inv.child || {};
                    const age = computeAgeAtPeriod(child.birthDate, inv.periodStartYear || periodStartYear);
                    return (
                        <tr key={i}>
                            <td style={{ padding: '4px 10px' }}>{inv.registrationNumber || inv.queueNumber || '—'}</td>
                            <td style={{ padding: '4px 10px' }}>
                                {[child.lastName, child.firstName, child.secondName].filter(Boolean).join(' ') || '—'}
                            </td>
                            <td style={{ padding: '4px 10px' }}>{child.birthCertificateNumber || '—'}</td>
                            <td style={{ padding: '4px 10px' }}>
                                <span className="badge bg-light text-dark border">{PLACE_TYPE_LABELS_PM[inv.placeType] || inv.placeType}</span>
                            </td>
                            <td style={{ padding: '4px 10px' }}>{formatBirthDate(child.birthDate)}</td>
                            <td style={{ padding: '4px 10px' }}>{formatAge(age)}</td>
                            <td style={{ padding: '4px 10px' }}>
                                <span className={'badge ' + (INVITE_STATUS_CLS_PM[inv.status] || 'bg-secondary')}>
                                    {INVITE_STATUS_LABELS_PM[inv.status] || inv.status}
                                </span>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

/**
 * Блок однієї групи всередині садка — характеристики + типи місць + діти.
 * Використовується у вкладці "Запрошення".
 */
function GroupBlock({ groupSlots, groupInvites }) {
    const [expanded, setExpanded] = useState(false);
    const first = groupSlots[0];
    const agg = aggregateSlotsList(groupSlots);

    return (
        <div className="mb-2" style={{ border: '1px solid #e9ecef', borderRadius: 6 }}>
            <div
                className="d-flex justify-content-between align-items-center"
                style={{ padding: '8px 12px', cursor: 'pointer', background: '#fff' }}
                onClick={() => setExpanded(!expanded)}
            >
                <div className="d-flex align-items-center gap-2">
                    <ExpandArrow expanded={expanded} />
                    <span style={{ fontWeight: 600, fontSize: 13 }}>Група {first.groupNumber}</span>
                    <span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>
                        {ageBracketLabel(first.ageFrom, first.ageTo)}
                    </span>
                    <span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>
                        {first.groupSpecialization || 'без спеціалізації'}
                    </span>
                    {groupSlots.map(s => (
                        <span key={s.placeType} className="badge bg-secondary-subtle text-dark" style={{ fontSize: 11 }}>
                            {PLACE_TYPE_LABELS_PM[s.placeType] || s.placeType}: {s.assignedCount}/{s.totalVacancies}
                        </span>
                    ))}
                </div>
                <MiniStats agg={agg} />
            </div>
            {expanded && (
                <div style={{ padding: '10px 12px', background: '#f8f9fa', borderTop: '1px solid #e9ecef' }}>
                    <InvitesTable invites={groupInvites} periodStartYear={first.periodStartYear} />
                </div>
            )}
        </div>
    );
}

/**
 * Блок садка — заголовок з агрегатом + розгортання.
 * mode='invites' → показує групи з дітьми (вкладка "Запрошення")
 * mode='breakdown' → показує розбивку по типу місця/віку (вкладка "Місця та вік")
 */
function KindergartenBlock({ kg, slots, invites, mode, breakdownBy }) {
    const [expanded, setExpanded] = useState(false);
    const kgSlots   = slots.filter(s => s.kindergartenId === kg.kindergartenId);
    const kgInvites = invites.filter(i => i.kindergartenId.toString() === kg.kindergartenId);
    const agg = aggregateSlotsList(kgSlots);

    // Групуємо слоти по groupNumber+period для режиму invites
    const groupsMap = {};
    if (mode === 'invites') {
        for (const s of kgSlots) {
            const key = `${s.groupNumber}__${s.periodStartYear}`;
            if (!groupsMap[key]) groupsMap[key] = [];
            groupsMap[key].push(s);
        }
    }

    const breakdownRows = mode === 'breakdown'
        ? (breakdownBy === 'placeType'
            ? breakdownSlotsBy(kgSlots, s => s.placeType, s => PLACE_TYPE_LABELS_PM[s.placeType] || s.placeType)
            : breakdownSlotsBy(kgSlots, s => `${s.ageFrom}-${s.ageTo}`, s => ageBracketLabel(s.ageFrom, s.ageTo))
          ).sort((a, b) => a.label.localeCompare(b.label))
        : [];

    return (
        <div className="mb-2" style={{ border: '1px solid #dee2e6', borderRadius: 6, background: '#fff' }}>
            <div
                className="d-flex justify-content-between align-items-center"
                style={{ padding: '10px 14px', cursor: 'pointer' }}
                onClick={() => setExpanded(!expanded)}
            >
                <div className="d-flex align-items-center gap-2">
                    <ExpandArrow expanded={expanded} />
                    <span style={{ fontWeight: 600, fontSize: 13 }}>№{kg.kindergartenNumber}</span>
                    {kg.kindergartenName && <span style={{ fontSize: 12, color: '#6c757d' }}>{kg.kindergartenName}</span>}
                </div>
                <MiniStats agg={agg} />
            </div>
            {expanded && (
                <div style={{ padding: '10px 14px 14px 34px', borderTop: '1px solid #e9ecef' }}>
                    {mode === 'invites'
                        ? (Object.keys(groupsMap).length === 0
                            ? <Empty text="Немає груп з вакансіями" />
                            : Object.entries(groupsMap)
                                .sort(([a], [b]) => a.localeCompare(b))
                                .map(([key, gSlots]) => (
                                    <GroupBlock
                                        key={key}
                                        groupSlots={gSlots}
                                        groupInvites={kgInvites.filter(i =>
                                            i.groupNumber === gSlots[0].groupNumber &&
                                            i.periodStartYear === gSlots[0].periodStartYear
                                        )}
                                    />
                                ))
                          )
                        : <BreakdownTable rows={breakdownRows} keyLabel={breakdownBy === 'placeType' ? 'Тип місця' : 'Вік'} />
                    }
                </div>
            )}
        </div>
    );
}

/**
 * Блок району — заголовок з агрегатом + список садків.
 */
function DistrictBlock({ district, slots, invites, mode, breakdownBy, search }) {
    const [expanded, setExpanded] = useState(false);
    const distSlots = slots.filter(s => (s.district || 'Не вказано') === district);
    const agg = aggregateSlotsList(distSlots);

    const kgMap = {};
    for (const s of distSlots) {
        if (!kgMap[s.kindergartenId]) {
            kgMap[s.kindergartenId] = {
                kindergartenId: s.kindergartenId,
                kindergartenNumber: s.kindergartenNumber,
                kindergartenName: s.kindergartenName,
            };
        }
    }
    let kgList = Object.values(kgMap);
    if (search) {
        const q = search.toLowerCase();
        kgList = kgList.filter(kg =>
            (kg.kindergartenNumber && kg.kindergartenNumber.toLowerCase().includes(q)) ||
            (kg.kindergartenName && kg.kindergartenName.toLowerCase().includes(q))
        );
    }
    kgList.sort((a, b) => (a.kindergartenNumber || '').localeCompare(b.kindergartenNumber || '', undefined, { numeric: true }));

    if (search && kgList.length === 0) return null;

    return (
        <div className="mb-2" style={{ border: '1px solid #dee2e6', borderRadius: 8, background: '#f8f9fa' }}>
            <div
                className="d-flex justify-content-between align-items-center"
                style={{ padding: '12px 16px', cursor: 'pointer' }}
                onClick={() => setExpanded(!expanded)}
            >
                <div className="d-flex align-items-center gap-2">
                    <ExpandArrow expanded={expanded} />
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{district}</span>
                    <span className="badge bg-secondary-subtle text-dark">{kgList.length} садків</span>
                </div>
                <MiniStats agg={agg} />
            </div>
            {expanded && (
                <div style={{ padding: '0 16px 14px' }}>
                    {kgList.map(kg => (
                        <KindergartenBlock
                            key={kg.kindergartenId}
                            kg={kg}
                            slots={distSlots}
                            invites={invites}
                            mode={mode}
                            breakdownBy={breakdownBy}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * Кореневий компонент дерева Місто → Район → Садок → (Групи+діти | Розбивка).
 */
function LocationDrillDown({ slots, invites, mode, breakdownBy }) {
    const [search, setSearch] = useState('');
    const cityAgg = aggregateSlotsList(slots);

    const districts = [...new Set(slots.map(s => s.district || 'Не вказано'))].sort();

    const cityBreakdown = mode === 'breakdown'
        ? (breakdownBy === 'placeType'
            ? breakdownSlotsBy(slots, s => s.placeType, s => PLACE_TYPE_LABELS_PM[s.placeType] || s.placeType)
            : breakdownSlotsBy(slots, s => `${s.ageFrom}-${s.ageTo}`, s => ageBracketLabel(s.ageFrom, s.ageTo))
          ).sort((a, b) => a.label.localeCompare(b.label))
        : [];

    if (slots.length === 0) {
        return <Empty text="Немає даних для цього періоду" />;
    }

    return (
        <div>
            <div className="mb-3">
                <input
                    className="form-control"
                    placeholder="Пошук за номером або назвою садка..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ maxWidth: 380 }}
                />
            </div>

            {/* По місту — завжди видимий підсумок */}
            <div className="mb-3" style={{ border: '2px solid #0d6efd', borderRadius: 8, background: '#eef5ff' }}>
                <div className="d-flex justify-content-between align-items-center" style={{ padding: '12px 16px' }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#0d6efd' }}>По місту (всього)</span>
                    <MiniStats agg={cityAgg} />
                </div>
                {mode === 'breakdown' && (
                    <div style={{ padding: '0 16px 14px' }}>
                        <BreakdownTable rows={cityBreakdown} keyLabel={breakdownBy === 'placeType' ? 'Тип місця' : 'Вік'} />
                    </div>
                )}
            </div>

            {districts.map(d => (
                <DistrictBlock
                    key={d}
                    district={d}
                    slots={slots}
                    invites={invites}
                    mode={mode}
                    breakdownBy={breakdownBy}
                    search={search}
                />
            ))}
        </div>
    );
}

// ─────────────────────────────────────────────
// PlanModal
// ─────────────────────────────────────────────

function PlanModal({ plan, onClose, isSimulation = false }) {
    const [tab, setTab] = useState('summary');
    const [fullPlan, setFullPlan] = useState(isSimulation ? plan : null);
    const [loadingFull, setLoadingFull] = useState(!isSimulation);
    const [notifications, setNotifications] = useState(null);
    const [loadingNotif, setLoadingNotif] = useState(false);
    const [rescheduling, setRescheduling] = useState(false);
    const [retrying, setRetrying] = useState(false);
    // 'all' — обидва роки разом, 'current' — тільки поточний, 'next' — тільки наступний
    const [periodView, setPeriodView] = useState('current');
    // Групування для вкладки "Місця та вік"
    const [breakdownBy, setBreakdownBy] = useState('placeType');

    // Симуляція вже містить повні дані (slots/invites без обрізки) —
    // довантажувати getOne не потрібно, бо запису в БД не існує.
    useEffect(() => {
        if (isSimulation) return;
        setLoadingFull(true);
        invitePlansApi.getOne(plan._id)
            .then(res => setFullPlan(res.data || plan))
            .catch(() => setFullPlan(plan))
            .finally(() => setLoadingFull(false));
    }, [plan._id]);

    const data = fullPlan || plan;

    const currentYear = data.periodBreakdown && data.periodBreakdown.current && data.periodBreakdown.current.periodStartYear;
    const nextYear    = data.periodBreakdown && data.periodBreakdown.next    && data.periodBreakdown.next.periodStartYear;
    const nextHasData = data.periodBreakdown && data.periodBreakdown.next &&
        data.periodBreakdown.next.stats && data.periodBreakdown.next.stats.totalSlots > 0;

    // Якщо даних наступного року немає — перемикач періоду не потрібен,
    // працюємо тільки з поточним роком (це і є "один навчальний рік" сценарій).
    const hasTwoPeriods = !!nextHasData;

    // Дані для зведення (картки/воронка) — залежать від periodView
    const viewData = React.useMemo(() => {
        if (!hasTwoPeriods) return data; // тільки один рік — завжди повні дані
        if (periodView === 'all' || !data.periodBreakdown) return data;
        const slice = data.periodBreakdown[periodView];
        if (!slice) return data;
        return {
            ...data,
            stats:          slice.stats,
            kgStats:        slice.kgStats,
            districtStats:  slice.districtStats,
            placeTypeStats: slice.placeTypeStats,
            ageStats:       slice.ageStats,
            requestStats:   slice.requestStats,
        };
    }, [data, periodView, hasTwoPeriods]);

    // Слоти/заявки відфільтровані під поточний вибір періоду —
    // для дерев "Запрошення" та "Місця та вік"
    const filteredSlots = React.useMemo(() => {
        const allSlots = data.slots || [];
        if (!hasTwoPeriods || periodView === 'all') return allSlots;
        const targetYear = periodView === 'current' ? currentYear : nextYear;
        return allSlots.filter(s => s.periodStartYear === targetYear);
    }, [data.slots, periodView, hasTwoPeriods, currentYear, nextYear]);

    const filteredInvites = React.useMemo(() => {
        const allInvites = data.invites || [];
        if (!hasTwoPeriods || periodView === 'all') return allInvites;
        const targetYear = periodView === 'current' ? currentYear : nextYear;
        return allInvites.filter(i => i.periodStartYear === targetYear);
    }, [data.invites, periodView, hasTwoPeriods, currentYear, nextYear]);

    useEffect(() => {
        if (tab === 'notifications' && !notifications) {
            setLoadingNotif(true);
            invitePlansApi.getNotifications(plan._id)
                .then(res => setNotifications(res.data || {}))
                .catch(() => setNotifications({}))
                .finally(() => setLoadingNotif(false));
        }
    }, [tab, plan._id, notifications]);

    const handleReschedule = useCallback(() => {
        const t = prompt('Час відправки (YYYY-MM-DDTHH:MM):');
        if (!t) return;
        setRescheduling(true);
        invitePlansApi.rescheduleNotifications(plan._id, new Date(t).toISOString())
            .then(() => { setNotifications(null); setTab('notifications'); })
            .finally(() => setRescheduling(false));
    }, [plan._id]);

    const handleRetry = useCallback(() => {
        setRetrying(true);
        invitePlansApi.retryNotifications(plan._id)
            .then(() => { setNotifications(null); setTab('notifications'); })
            .finally(() => setRetrying(false));
    }, [plan._id]);

    const s = viewData.stats || {};

    const TABS = [
        { key: 'summary',       label: 'Зведення' },
        { key: 'invites',       label: 'Запрошення' },
        { key: 'placesAge',     label: 'Місця та вік' },
        ...(!isSimulation ? [
            { key: 'notifications', label: 'Сповіщення' },
            { key: 'errors',        label: `Помилки${data.validationErrors && data.validationErrors.length ? ' (' + data.validationErrors.length + ')' : ''}` },
        ] : []),
    ];

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1050, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 40, overflowY: 'auto' }}>
            <div style={{ background: '#fff', borderRadius: 10, width: '94%', maxWidth: 1200, boxShadow: '0 8px 40px rgba(0,0,0,.18)', marginBottom: 40 }}>
                {/* Header */}
                <div style={{ padding: '20px 24px 0', borderBottom: '1px solid #dee2e6' }}>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                {isSimulation
                                    ? <span className="badge bg-primary-subtle text-primary border border-primary">👁 Симуляція (не зберігається)</span>
                                    : <StatusBadge status={plan.status} />
                                }
                                {!isSimulation && (
                                    <span className="badge bg-light text-dark border">{TRIGGER_LABELS[plan.triggerType] || plan.triggerType}</span>
                                )}
                                {!hasTwoPeriods && currentYear && (
                                    <span style={{ fontSize: 13, color: '#6c757d' }}>{currentYear}/{currentYear + 1}</span>
                                )}
                            </div>
                            <div style={{ fontSize: 13, color: '#6c757d' }}>
                                {isSimulation
                                    ? <>Розраховано: {formatDate(plan.simulatedAt)} · Це знімок стану на цей момент, він не зберігається</>
                                    : <>
                                        Створено: {formatDate(plan.createdAt)}
                                        {plan.committedAt && <> · Завершено: {formatDate(plan.committedAt)}</>}
                                        {s.totalDurationMs && <> · Тривалість: {formatDuration(s.totalDurationMs)}</>}
                                      </>
                                }
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-3">
                            {/* Перемикач навчального року — тільки якщо реально є два активні роки */}
                            {hasTwoPeriods && (
                                <div>
                                    <div style={{ fontSize: 11, color: '#6c757d', textAlign: 'right', marginBottom: 2 }}>Навчальний рік</div>
                                    <div className="btn-group btn-group-sm" role="group">
                                        <button
                                            className={'btn ' + (periodView === 'current' ? 'btn-primary' : 'btn-outline-secondary')}
                                            onClick={() => setPeriodView('current')}
                                        >{currentYear}/{currentYear + 1}</button>
                                        <button
                                            className={'btn ' + (periodView === 'next' ? 'btn-primary' : 'btn-outline-secondary')}
                                            onClick={() => setPeriodView('next')}
                                        >{nextYear}/{nextYear + 1}</button>
                                        <button
                                            className={'btn ' + (periodView === 'all' ? 'btn-primary' : 'btn-outline-secondary')}
                                            onClick={() => setPeriodView('all')}
                                        >Разом</button>
                                    </div>
                                </div>
                            )}
                            <button className="btn-close" onClick={onClose} />
                        </div>
                    </div>

                    {/* Tabs */}
                    <ul className="nav nav-tabs border-0">
                        {TABS.map(t => (
                            <li key={t.key} className="nav-item">
                                <button
                                    className={'nav-link' + (tab === t.key ? ' active' : '')}
                                    style={{ border: 'none', borderBottom: tab === t.key ? '2px solid #0d6efd' : '2px solid transparent', borderRadius: 0, padding: '8px 16px' }}
                                    onClick={() => setTab(t.key)}
                                >{t.label}</button>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Body */}
                <div style={{ padding: '24px' }}>

                    {loadingFull && tab !== 'notifications' ? (
                        <Spinner />
                    ) : (
                    <>

                    {/* ── Summary ───────────────────────────── */}
                    {tab === 'summary' && (
                        <div>
                            <div className="row g-3 mb-4">
                                {[
                                    { label: 'Вакансій всього',       value: s.totalVacancies || 0,       color: '#0d6efd' },
                                    { label: 'Запрошень відправлено', value: s.committedInvites || 0,     color: '#198754' },
                                    { label: 'Пропущено',             value: s.skippedInvites || 0,       color: '#6c757d' },
                                    { label: 'Помилок',               value: s.errorInvites || 0,         color: '#dc3545' },
                                    { label: 'Заявок завантажено',    value: s.totalRequestsLoaded || 0,  color: '#0dcaf0' },
                                    { label: 'Садків з вакансіями',   value: s.totalKindergartens || 0,   color: '#6f42c1' },
                                ].map(c => (
                                    <div key={c.label} className="col-6 col-md-4 col-lg-2">
                                        <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '16px 12px', textAlign: 'center', borderTop: '3px solid ' + c.color }}>
                                            <div style={{ fontSize: 28, fontWeight: 700, color: c.color }}>{c.value}</div>
                                            <div style={{ fontSize: 12, color: '#6c757d', marginTop: 2 }}>{c.label}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="row g-3 mb-4">
                                <div className="col-12 col-md-6">
                                    <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 20 }}>
                                        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Заповненість вакансій</div>
                                        <FillRateBar value={s.overallFillRate} />
                                        <div className="row mt-3" style={{ fontSize: 13 }}>
                                            <div className="col-4 text-center">
                                                <div style={{ fontWeight: 600, color: '#198754' }}>{s.filledSlots || 0}</div>
                                                <div className="text-muted">Заповнено</div>
                                            </div>
                                            <div className="col-4 text-center">
                                                <div style={{ fontWeight: 600, color: '#ffc107' }}>{s.partialSlots || 0}</div>
                                                <div className="text-muted">Частково</div>
                                            </div>
                                            <div className="col-4 text-center">
                                                <div style={{ fontWeight: 600, color: '#dc3545' }}>{s.emptySlots || 0}</div>
                                                <div className="text-muted">Порожніх</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {viewData.requestStats && (
                                    <div className="col-12 col-md-6">
                                        <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 20 }}>
                                            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Воронка заявок</div>
                                            {[
                                                { label: 'Завантажено',          value: viewData.requestStats.totalLoaded || 0, color: '#0d6efd' },
                                                { label: 'Підійшли (є збіг)',     value: (viewData.requestStats.gotInvite || 0) + (viewData.requestStats.noVacancy || 0), color: '#6f42c1' },
                                                { label: 'Отримали запрошення',   value: viewData.requestStats.gotInvite || 0,   color: '#198754' },
                                                { label: 'Немає вакансій',        value: viewData.requestStats.noVacancy || 0,   color: '#ffc107' },
                                                { label: 'Немає збігу',           value: viewData.requestStats.noMatch || 0,     color: '#dc3545' },
                                            ].map(r => (
                                                <div key={r.label} className="d-flex justify-content-between align-items-center mb-2" style={{ fontSize: 13 }}>
                                                    <span style={{ color: '#495057' }}>{r.label}</span>
                                                    <span style={{ fontWeight: 700, color: r.color, minWidth: 50, textAlign: 'right' }}>{r.value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 16 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: '#495057' }}>Часові метрики</div>
                                <div className="row" style={{ fontSize: 13 }}>
                                    {[
                                        { label: 'Планування',  value: formatDuration(s.planningDurationMs) },
                                        { label: 'Валідація',   value: formatDuration(s.validationDurationMs) },
                                        { label: 'Запис',       value: formatDuration(s.commitDurationMs) },
                                        { label: 'Всього',      value: formatDuration(s.totalDurationMs) },
                                    ].map(m => (
                                        <div key={m.label} className="col-6 col-md-3 text-center mb-2">
                                            <div style={{ fontWeight: 600 }}>{m.value}</div>
                                            <div className="text-muted">{m.label}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Запрошення (Райони → Садки → Групи → Діти) ──── */}
                    {tab === 'invites' && (
                        <LocationDrillDown
                            slots={filteredSlots}
                            invites={filteredInvites}
                            mode="invites"
                        />
                    )}

                    {/* ── Місця та вік (Райони → Садки → розбивка) ────── */}
                    {tab === 'placesAge' && (
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <span style={{ fontSize: 13, color: '#6c757d' }}>Групувати за:</span>
                                <div className="btn-group btn-group-sm" role="group">
                                    <button
                                        className={'btn ' + (breakdownBy === 'placeType' ? 'btn-primary' : 'btn-outline-secondary')}
                                        onClick={() => setBreakdownBy('placeType')}
                                    >Типом місця</button>
                                    <button
                                        className={'btn ' + (breakdownBy === 'age' ? 'btn-primary' : 'btn-outline-secondary')}
                                        onClick={() => setBreakdownBy('age')}
                                    >Віком</button>
                                </div>
                            </div>
                            <LocationDrillDown
                                slots={filteredSlots}
                                invites={filteredInvites}
                                mode="breakdown"
                                breakdownBy={breakdownBy}
                            />
                        </div>
                    )}

                    {/* ── Notifications ─────────────────────── */}
                    {tab === 'notifications' && (
                        <div>
                            {loadingNotif ? <Spinner /> : notifications && (
                                <div>
                                    <div className="row g-3 mb-4">
                                        {[
                                            { label: 'Email: очікують',  value: notifications.emailPending || 0, color: '#ffc107' },
                                            { label: 'Email: відправлено', value: notifications.emailSent || 0,   color: '#198754' },
                                            { label: 'Email: помилки',   value: notifications.emailFailed || 0,  color: '#dc3545' },
                                            { label: 'SMS: очікують',    value: notifications.smsPending || 0,   color: '#ffc107' },
                                            { label: 'SMS: відправлено', value: notifications.smsSent || 0,      color: '#198754' },
                                            { label: 'SMS: помилки',     value: notifications.smsFailed || 0,    color: '#dc3545' },
                                        ].map(c => (
                                            <div key={c.label} className="col-6 col-md-4 col-lg-2">
                                                <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '14px 10px', textAlign: 'center', borderTop: '3px solid ' + c.color }}>
                                                    <div style={{ fontSize: 24, fontWeight: 700, color: c.color }}>{c.value}</div>
                                                    <div style={{ fontSize: 11, color: '#6c757d', marginTop: 2 }}>{c.label}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="d-flex gap-2">
                                        <button
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={handleReschedule}
                                            disabled={rescheduling}
                                        >
                                            {rescheduling ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                                            Перепланувати відправку
                                        </button>
                                        {(notifications.emailFailed > 0 || notifications.smsFailed > 0) && (
                                            <button
                                                className="btn btn-outline-warning btn-sm"
                                                onClick={handleRetry}
                                                disabled={retrying}
                                            >
                                                {retrying ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                                                Повторити помилки
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Errors ───────────────────────────── */}
                    {tab === 'errors' && (
                        <div>
                            {(!data.validationErrors || data.validationErrors.length === 0)
                                ? <Empty text="Помилок валідації немає" />
                                : (
                                    <div className="table-responsive" style={{ maxHeight: 480, overflowY: 'auto' }}>
                                        <table className="table table-sm mb-0">
                                            <thead style={{ position: 'sticky', top: 0, background: '#fff' }}>
                                                <tr style={{ borderBottom: '2px solid #dee2e6', fontSize: 13 }}>
                                                    <th style={{ padding: '8px 12px' }}>Тип</th>
                                                    <th style={{ padding: '8px 12px' }}>Група</th>
                                                    <th style={{ padding: '8px 12px' }}>Деталі</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.validationErrors.map((e, i) => (
                                                    <tr key={i} style={{ fontSize: 13 }}>
                                                        <td style={{ padding: '6px 12px' }}>
                                                            <span className="badge bg-danger-subtle text-danger">{ERROR_TYPE_LABELS_PM[e.type] || e.type}</span>
                                                        </td>
                                                        <td style={{ padding: '6px 12px' }}>{e.groupNumber || '—'}</td>
                                                        <td style={{ padding: '6px 12px', color: '#6c757d' }}>{e.details || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )
                            }
                        </div>
                    )}
                    </>
                    )}
                </div>
            </div>
        </div>
    );
}

function StatsTable({ rows, columns, defaultSort, defaultDir, emptyText }) {
    const [sortConfig, setSortConfig] = useState({ key: defaultSort || columns[0].key, direction: defaultDir || 'desc' });

    const sorted = React.useMemo(() => {
        if (!rows || rows.length === 0) return [];
        return [...rows].sort((a, b) => {
            const av = a[sortConfig.key], bv = b[sortConfig.key];
            if (av == null) return 1;
            if (bv == null) return -1;
            const cmp = av < bv ? -1 : av > bv ? 1 : 0;
            return sortConfig.direction === 'asc' ? cmp : -cmp;
        });
    }, [rows, sortConfig]);

    const toggle = (key) => setSortConfig(prev => ({
        key,
        direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));

    if (!rows || rows.length === 0) return <Empty text={emptyText} />;

    return (
        <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
                <thead>
                    <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                        {columns.map(col => (
                            <th
                                key={col.key}
                                onClick={() => toggle(col.key)}
                                style={{ cursor: 'pointer', userSelect: 'none', fontWeight: 600, fontSize: 13, padding: '8px 12px', whiteSpace: 'nowrap' }}
                            >
                                {col.label}<SortIcon col={col.key} sortConfig={sortConfig} />
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((row, i) => (
                        <tr key={i} style={{ fontSize: 13 }}>
                            {columns.map(col => (
                                <td key={col.key} style={{ padding: '6px 12px', verticalAlign: 'middle', minWidth: col.key === 'fillRate' ? 140 : undefined }}>
                                    {col.render(row)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ─────────────────────────────────────────────
// Графік тренду fillRate
// ─────────────────────────────────────────────

function TrendChart({ data }) {
    if (!data || data.length === 0) return null;

    const reversed = [...data].reverse();
    const maxFill = 100;
    const h = 80;
    const w = 100;
    const pts = reversed.map((d, i) => {
        const x = data.length === 1 ? 50 : (i / (reversed.length - 1)) * w;
        const y = h - ((d.stats && d.stats.overallFillRate || 0) / maxFill) * h;
        return `${x},${y}`;
    }).join(' ');

    return (
        <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#495057', marginBottom: 8 }}>
                Динаміка заповненості (останні {data.length} запусків)
            </div>
            <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 16px' }}>
                <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 80 }} preserveAspectRatio="none">
                    {[0, 25, 50, 75, 100].map(v => (
                        <line key={v} x1="0" y1={h - v * h / 100} x2={w} y2={h - v * h / 100}
                            stroke="#dee2e6" strokeWidth="0.5" />
                    ))}
                    <polyline points={pts} fill="none" stroke="#0d6efd" strokeWidth="1.5" />
                    {reversed.map((d, i) => {
                        const x = data.length === 1 ? 50 : (i / (reversed.length - 1)) * w;
                        const y = h - ((d.stats && d.stats.overallFillRate || 0) / maxFill) * h;
                        return <circle key={i} cx={x} cy={y} r="1.5" fill="#0d6efd" />;
                    })}
                </svg>
                <div className="d-flex justify-content-between" style={{ fontSize: 11, color: '#6c757d', marginTop: 4 }}>
                    <span>{formatDate(reversed[0] && reversed[0].committedAt)}</span>
                    <span>{formatDate(reversed[reversed.length - 1] && reversed[reversed.length - 1].committedAt)}</span>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────
// Панель управління шедулером
// ─────────────────────────────────────────────

function SchedulerPanel({ activePlan, onRunPlan, onSimulate, running, simulating }) {
    return (
        <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '16px 20px', marginBottom: 24 }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Управління плануванням</div>
                    {activePlan
                        ? <div className="d-flex align-items-center gap-2" style={{ fontSize: 13, color: '#856404' }}>
                            <div className="spinner-border spinner-border-sm text-warning" role="status" />
                            <span>Активний план: <StatusBadge status={activePlan.status} /></span>
                          </div>
                        : <div style={{ fontSize: 13, color: '#6c757d' }}>
                            Активних планів немає. «Симуляція» показує поточну картину без запису — корисно щоб оцінити ситуацію не чекаючи реальної розсилки.
                          </div>
                    }
                </div>
                <div className="d-flex align-items-center gap-2">
                    <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={onSimulate}
                        disabled={simulating || running}
                        title="Показати поточну картину розподілу без запису в базу"
                    >
                        {simulating
                            ? <><span className="spinner-border spinner-border-sm me-1" />Рахується...</>
                            : '👁 Симулювати (без запису)'
                        }
                    </button>
                    <button
                        className="btn btn-primary btn-sm"
                        onClick={onRunPlan}
                        disabled={running || simulating || !!activePlan}
                        title="Реально відправити запрошення і зафіксувати результат"
                    >
                        {running
                            ? <><span className="spinner-border spinner-border-sm me-1" />Запускається...</>
                            : '▶ Запустити розсилку'
                        }
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────
// Панель "Коли наступна розсилка"
// ─────────────────────────────────────────────

function formatRelativeTime(dateStr, now) {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const diffMs = date - now;
    if (diffMs <= 0) return 'з хвилини на хвилину';

    const diffMin = Math.round(diffMs / 60000);
    if (diffMin < 60) return `через ${diffMin} хв`;

    const diffHours = Math.round(diffMin / 60);
    if (diffHours < 24) return `через ${diffHours} год`;

    const diffDays = Math.round(diffHours / 24);
    return `через ${diffDays} дн`;
}

function QueueHandlerJobCard({ job, now }) {
    if (!job) {
        return (
            <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#6c757d' }}>
                Джоб queueHandler не знайдено в шедулері (можливо процес щойно перезапущено)
            </div>
        );
    }

    if (job.disabled) {
        return (
            <div style={{ background: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 8, padding: '12px 16px' }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>queueHandler — вимкнено</div>
                <div style={{ fontSize: 12, color: '#842029' }}>{job.disabledReason}</div>
            </div>
        );
    }

    const relative = job.nextRun ? formatRelativeTime(job.nextRun, now) : null;
    return (
        <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 16px' }}>
            <div className="d-flex justify-content-between align-items-center">
                <div>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>Наступний запуск queueHandler</span>
                    {job.runsNow && <span className="badge bg-warning text-dark ms-2">Виконується зараз</span>}
                </div>
                <div className="text-end">
                    <div style={{ fontWeight: 600 }}>{formatDate(job.nextRun)}</div>
                    <div style={{ fontSize: 11, color: '#6c757d' }}>{relative}</div>
                </div>
            </div>
            <div style={{ fontSize: 11, color: '#6c757d', marginTop: 6 }}>
                Вікно запуску: {job.afterHours}:00 — {job.beforeHours}:00
                {job.lastRunTime && <> · Останній запуск: {formatDate(job.lastRunTime)}</>}
            </div>
        </div>
    );
}

function PeriodStatusCard({ title, status, now }) {
    const isStopped = status.stopped;
    return (
        <div style={{
            background: isStopped ? '#fff3cd' : '#d1e7dd',
            border: '1px solid ' + (isStopped ? '#ffe69c' : '#a3cfbb'),
            borderRadius: 8,
            padding: '12px 16px',
        }}>
            <div className="d-flex align-items-center justify-content-between mb-1">
                <span style={{ fontWeight: 600, fontSize: 13 }}>{title}</span>
                <span className={'badge ' + (isStopped ? 'bg-warning text-dark' : 'bg-success')}>
                    {isStopped ? 'Призупинено' : 'Активно'}
                </span>
            </div>
            {isStopped && (
                <div style={{ fontSize: 12, color: '#664d03' }}>
                    {status.reason && <div>Причина: {status.reason}</div>}
                    {status.nextAvailableAt && (
                        <div>Відновиться: {formatDate(status.nextAvailableAt)} ({formatRelativeTime(status.nextAvailableAt, now)})</div>
                    )}
                </div>
            )}
        </div>
    );
}

function NextMailingPanel() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        invitePlansApi.getSchedulerStatus()
            .then(res => setStatus(res.data))
            .catch(() => setError('Не вдалось завантажити стан шедулера'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="mb-4"><Spinner /></div>;
    if (error) return <div className="mb-4"><ErrorMsg msg={error} /></div>;
    if (!status) return null;

    const now = status.serverTime ? new Date(status.serverTime) : new Date();

    return (
        <div style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: 20, marginBottom: 24 }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div style={{ fontWeight: 700, fontSize: 15 }}>Коли наступна розсилка</div>
                <button className="btn btn-outline-secondary btn-sm" onClick={load}>↻</button>
            </div>

            <div className="row g-3 mb-3">
                <div className="col-12 col-md-6">
                    <PeriodStatusCard title="Поточний навчальний рік" status={status.distribution.currentPeriod} now={now} />
                </div>
                <div className="col-12 col-md-6">
                    <PeriodStatusCard title="Наступний навчальний рік" status={status.distribution.nextPeriod} now={now} />
                </div>
            </div>

            <div className="mb-3">
                <QueueHandlerJobCard job={status.queueHandlerJob} now={now} />
            </div>

            {status.lastCommittedPlan && (
                <div style={{ fontSize: 12, color: '#6c757d' }}>
                    Останній реальний запуск: {formatDate(status.lastCommittedPlan.committedAt)} ·
                    {' '}відправлено {status.lastCommittedPlan.stats && status.lastCommittedPlan.stats.committedInvites || 0} запрошень
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────
// Панель "Відкриті/закриті садки на прийом"
// ─────────────────────────────────────────────

function ReceptionDistrictRow({ district }) {
    const [expanded, setExpanded] = useState(false);
    return (
        <div className="mb-2" style={{ border: '1px solid #dee2e6', borderRadius: 8, background: '#f8f9fa' }}>
            <div
                className="d-flex justify-content-between align-items-center"
                style={{ padding: '10px 14px', cursor: 'pointer' }}
                onClick={() => setExpanded(!expanded)}
            >
                <div className="d-flex align-items-center gap-2">
                    <ExpandArrow expanded={expanded} />
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{district.district}</span>
                </div>
                <div className="d-flex align-items-center gap-3" style={{ fontSize: 12 }}>
                    <span style={{ color: '#198754' }}>Відкрито: <b>{district.openCount}</b></span>
                    <span style={{ color: '#dc3545' }}>Закрито: <b>{district.closedCount}</b></span>
                    <span className="text-muted">Всього: {district.totalCount}</span>
                </div>
            </div>
            {expanded && (
                <div style={{ padding: '0 14px 12px 34px' }}>
                    <table className="table table-sm mb-0" style={{ fontSize: 12, background: '#fff' }}>
                        <thead>
                            <tr style={{ color: '#6c757d' }}>
                                <th style={{ padding: '4px 10px', fontWeight: 500 }}>№ садка</th>
                                <th style={{ padding: '4px 10px', fontWeight: 500 }}>Назва</th>
                                <th style={{ padding: '4px 10px', fontWeight: 500 }}>Статус</th>
                            </tr>
                        </thead>
                        <tbody>
                            {district.kindergartens.map(kg => (
                                <tr key={kg.kindergartenId}>
                                    <td style={{ padding: '4px 10px' }}>№{kg.number}</td>
                                    <td style={{ padding: '4px 10px' }}>{kg.name || '—'}</td>
                                    <td style={{ padding: '4px 10px' }}>
                                        <span className={'badge ' + (kg.isOpen ? 'bg-success' : 'bg-secondary')}>
                                            {kg.isOpen ? 'Відкрито' : 'Закрито'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function ReceptionStatusPanel() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');

    const load = useCallback(() => {
        setLoading(true);
        invitePlansApi.getReceptionStatus()
            .then(res => setData(res.data))
            .catch(() => setError('Не вдалось завантажити статус прийому'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="mb-4"><Spinner /></div>;
    if (error) return <div className="mb-4"><ErrorMsg msg={error} /></div>;
    if (!data) return null;

    const filteredDistricts = (data.districts || []).filter(d => {
        if (!search) return true;
        const q = search.toLowerCase();
        return d.district.toLowerCase().includes(q) ||
            d.kindergartens.some(kg =>
                (kg.number && kg.number.toLowerCase().includes(q)) ||
                (kg.name && kg.name.toLowerCase().includes(q))
            );
    });

    return (
        <div style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: 20, marginBottom: 24 }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div style={{ fontWeight: 700, fontSize: 15 }}>Садки відкриті/закриті на прийом</div>
                <button className="btn btn-outline-secondary btn-sm" onClick={load}>↻</button>
            </div>

            <div className="row g-3 mb-3">
                <div className="col-6 col-md-4">
                    <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px', textAlign: 'center' }}>
                        <div style={{ fontSize: 22, fontWeight: 700 }}>{data.totals.totalCount}</div>
                        <div style={{ fontSize: 12, color: '#6c757d' }}>Всього садків</div>
                    </div>
                </div>
                <div className="col-6 col-md-4">
                    <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px', textAlign: 'center', borderTop: '3px solid #198754' }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: '#198754' }}>{data.totals.openCount}</div>
                        <div style={{ fontSize: 12, color: '#6c757d' }}>Відкрито на прийом</div>
                    </div>
                </div>
                <div className="col-6 col-md-4">
                    <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px', textAlign: 'center', borderTop: '3px solid #dc3545' }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: '#dc3545' }}>{data.totals.closedCount}</div>
                        <div style={{ fontSize: 12, color: '#6c757d' }}>Закрито</div>
                    </div>
                </div>
            </div>

            <input
                className="form-control mb-3"
                placeholder="Пошук за районом, номером або назвою садка..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ maxWidth: 380 }}
            />

            {filteredDistricts.length === 0
                ? <Empty text="Нічого не знайдено" />
                : filteredDistricts.map(d => <ReceptionDistrictRow key={d.district} district={d} />)
            }
        </div>
    );
}

// ─────────────────────────────────────────────
// Головний компонент — дашборд
// ─────────────────────────────────────────────

// ─────────────────────────────────────────────
// Пагінація
// ─────────────────────────────────────────────

function Pagination({ page, totalPages, onChange }) {
    if (totalPages <= 1) return null;

    const pages = [];
    const windowSize = 2;
    for (let p = 1; p <= totalPages; p++) {
        if (
            p === 1 || p === totalPages ||
            (p >= page - windowSize && p <= page + windowSize)
        ) {
            pages.push(p);
        } else if (pages[pages.length - 1] !== '...') {
            pages.push('...');
        }
    }

    return (
        <nav className="d-flex justify-content-center align-items-center gap-1 mt-3">
            <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page <= 1}
                onClick={() => onChange(page - 1)}
            >
                ‹
            </button>
            {pages.map((p, i) =>
                p === '...'
                    ? <span key={'dots' + i} style={{ padding: '0 6px', color: '#adb5bd' }}>…</span>
                    : (
                        <button
                            key={p}
                            className={'btn btn-sm ' + (p === page ? 'btn-primary' : 'btn-outline-secondary')}
                            onClick={() => onChange(p)}
                            style={{ minWidth: 34 }}
                        >
                            {p}
                        </button>
                    )
            )}
            <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page >= totalPages}
                onClick={() => onChange(page + 1)}
            >
                ›
            </button>
        </nav>
    );
}

// ─────────────────────────────────────────────
// Головний компонент — дашборд
// ─────────────────────────────────────────────

const InvitePlans = () => {
    const [plans, setPlans]           = useState([]);
    const [trend, setTrend]           = useState([]);
    const [loading, setLoading]       = useState(false);
    const [error, setError]           = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [running, setRunning]       = useState(false);
    const [simulating, setSimulating] = useState(false);
    const [simulatedPlan, setSimulatedPlan] = useState(null);
    const [runMsg, setRunMsg]         = useState(null);
    const [page, setPage]             = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal]           = useState(0);
    const PAGE_SIZE = 20;
    const { sorted, sortConfig, toggle } = useSort(plans, 'createdAt');

    const activePlan = plans.find(p =>
        p.status === 'planning' || p.status === 'validating' || p.status === 'committing'
    );

    const loadData = useCallback((targetPage) => {
        const p = targetPage || page;
        setLoading(true);
        setError(null);
        Promise.all([
            invitePlansApi.getList(p, PAGE_SIZE),
            invitePlansApi.getTrend(30),
        ])
            .then(([listRes, trendRes]) => {
                const listData = listRes.data || {};
                setPlans(listData.items || []);
                setTotal(listData.total || 0);
                setTotalPages(listData.totalPages || 1);
                setTrend(trendRes.data || []);
            })
            .catch(() => setError('Не вдалось завантажити дані'))
            .finally(() => setLoading(false));
    }, [page]);

    useEffect(() => { loadData(page); }, [page]);

    // Автооновлення якщо є активний план — тільки на поточній сторінці
    useEffect(() => {
        if (!activePlan) return;
        const timer = setInterval(() => loadData(page), 8000);
        return () => clearInterval(timer);
    }, [activePlan, loadData, page]);

    const handlePageChange = useCallback((newPage) => {
        setPage(newPage);
    }, []);

    const handleRun = useCallback(() => {
        setRunning(true);
        setRunMsg(null);
        invitePlansApi.run()
            .then(res => {
                setRunMsg(res.data && res.data.ok
                    ? { type: 'success', text: 'Розсилку запущено' }
                    : { type: 'warning', text: (res.data && res.data.reason) || 'Не вдалось запустити' }
                );
                setPage(1);
                loadData(1);
            })
            .catch(() => setRunMsg({ type: 'danger', text: 'Помилка запуску' }))
            .finally(() => setRunning(false));
    }, [loadData]);

    // Симуляція — нічого не пишеться в БД, результат живе тільки у стані компонента
    // і показується через ту саму модалку (з ознакою isSimulation, без спроби
    // довантажити getOne — дані вже повні).
    const handleSimulate = useCallback(() => {
        setSimulating(true);
        setRunMsg(null);
        invitePlansApi.simulate()
            .then(res => {
                if (res.data && res.data.ok) {
                    setSimulatedPlan(res.data);
                } else {
                    setRunMsg({ type: 'danger', text: (res.data && res.data.error) || 'Не вдалось порахувати симуляцію' });
                }
            })
            .catch(() => setRunMsg({ type: 'danger', text: 'Помилка симуляції' }))
            .finally(() => setSimulating(false));
    }, []);

    const TABLE_COLS = [
        { key: 'createdAt',        label: 'Дата запуску' },
        { key: 'status',           label: 'Статус' },
        { key: 'triggerType',      label: 'Тип' },
        { key: 'currentPeriodStartYear', label: 'Рік' },
        { key: 'committedInvites', label: 'Відправлено' },
        { key: 'overallFillRate',  label: 'Заповненість' },
        { key: 'totalDurationMs',  label: 'Тривалість' },
    ];

    return (
        <div className="container-fluid py-3">
            <div className="row">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="mb-0" style={{ fontWeight: 700 }}>Планування запрошень</h5>
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => loadData(page)} disabled={loading}>
                            {loading ? <span className="spinner-border spinner-border-sm" /> : '↻ Оновити'}
                        </button>
                    </div>

                    <NextMailingPanel />

                    <ReceptionStatusPanel />

                    <SchedulerPanel
                        activePlan={activePlan}
                        onRunPlan={handleRun}
                        onSimulate={handleSimulate}
                        running={running}
                        simulating={simulating}
                    />

                    {runMsg && (
                        <div className={'alert alert-' + runMsg.type + ' py-2 mb-3'} style={{ fontSize: 13 }}>
                            {runMsg.text}
                        </div>
                    )}

                    {error && <ErrorMsg msg={error} />}

                    <TrendChart data={trend} />

                    {loading && plans.length === 0 ? <Spinner /> : (
                        <div className="table-responsive">
                            <table className="table table-sm table-hover mb-0">
                                <thead>
                                    <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                        {TABLE_COLS.map(col => (
                                            <th
                                                key={col.key}
                                                onClick={() => toggle(col.key)}
                                                style={{ cursor: 'pointer', userSelect: 'none', fontWeight: 600, fontSize: 13, padding: '8px 12px', whiteSpace: 'nowrap' }}
                                            >
                                                {col.label}<SortIcon col={col.key} sortConfig={sortConfig} />
                                            </th>
                                        ))}
                                        <th style={{ padding: '8px 12px', fontWeight: 600, fontSize: 13 }}>Дії</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sorted.length === 0
                                        ? <tr><td colSpan={TABLE_COLS.length + 1}><Empty text="Запусків ще не було" /></td></tr>
                                        : sorted.map(plan => {
                                            const s = plan.stats || {};
                                            return (
                                                <tr
                                                    key={plan._id}
                                                    style={{ fontSize: 13, cursor: 'pointer' }}
                                                    onClick={() => setSelectedPlan(plan)}
                                                >
                                                    <td style={{ padding: '8px 12px' }}>{formatDate(plan.createdAt)}</td>
                                                    <td style={{ padding: '8px 12px' }}><StatusBadge status={plan.status} /></td>
                                                    <td style={{ padding: '8px 12px' }}>{TRIGGER_LABELS[plan.triggerType] || plan.triggerType}</td>
                                                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                                                        <div>{plan.currentPeriodStartYear}/{(plan.currentPeriodStartYear || 0) + 1}</div>
                                                        {plan.nextPeriodStartYear && (
                                                            <div style={{ fontSize: 11, color: '#6c757d' }}>
                                                                + {plan.nextPeriodStartYear}/{plan.nextPeriodStartYear + 1}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '8px 12px', fontWeight: 600, color: '#198754' }}>{s.committedInvites != null ? s.committedInvites : '—'}</td>
                                                    <td style={{ padding: '8px 12px', minWidth: 140 }}>
                                                        {plan.status === 'committed' ? <FillRateBar value={s.overallFillRate} /> : '—'}
                                                    </td>
                                                    <td style={{ padding: '8px 12px', color: '#6c757d' }}>{formatDuration(s.totalDurationMs)}</td>
                                                    <td style={{ padding: '8px 12px' }} onClick={e => e.stopPropagation()}>
                                                        <button
                                                            className="btn btn-link btn-sm p-0 text-primary"
                                                            onClick={() => setSelectedPlan(plan)}
                                                            style={{ fontSize: 13 }}
                                                        >
                                                            Деталі →
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    }
                                </tbody>
                            </table>
                            {total > 0 && (
                                <div className="d-flex justify-content-between align-items-center" style={{ fontSize: 12, color: '#6c757d', padding: '8px 12px' }}>
                                    <span>
                                        Показано {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} з {total}
                                    </span>
                                </div>
                            )}
                            <Pagination page={page} totalPages={totalPages} onChange={handlePageChange} />
                        </div>
                    )}
                </div>
            </div>

            {selectedPlan && (
                <PlanModal
                    plan={selectedPlan}
                    onClose={() => setSelectedPlan(null)}
                />
            )}

            {simulatedPlan && (
                <PlanModal
                    plan={simulatedPlan}
                    onClose={() => setSimulatedPlan(null)}
                    isSimulation
                />
            )}
        </div>
    );
};

export default InvitePlans;